import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, unref as _unref, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "text-body-2 mb-0"
};
const _hoisted_2 = {
    key: 0,
    class: "text-caption text-medium-emphasis mr-2"
};
const _hoisted_3 = { class: "d-flex flex-wrap justify-center ga-2" };
const _hoisted_4 = {
    key: 1,
    class: "text-caption text-medium-emphasis"
};
const _hoisted_5 = ["multiple", "accept"];
const _hoisted_6 = {
    key: 2,
    class: "text-body-2 text-medium-emphasis mt-2 mb-0"
};
const _hoisted_7 = {
    key: 5,
    class: "text-caption text-medium-emphasis mt-2 mb-0"
};
import { computed, nextTick, ref, watch } from "vue";
import ChartPrepareStatus from "@/components/ChartPrepareStatus.vue";
import { describeAccept, matchesAccept, parseAccept, usePastedFiles } from "@/composables/usePastedFiles";
import { CHART_ACCEPT, MAX_AI_ONLY_DOCUMENTS } from "@/composables/useChartUpload";
/** The one document list for PA uploads: every file is read by the AI; checked rows also go to the payer. */
export default /*@__PURE__*/ _defineComponent({
    __name: 'DocumentUploadList',
    props: {
        docs: {},
        disabled: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        budgetCaption: { default: null },
        canChoosePages: { type: Function, default: () => false },
        openLink: { type: [Function, null], default: null },
        accept: { default: CHART_ACCEPT }
    },
    emits: ["add-files", "upload-into", "remove", "update-send-to-payer", "update-label", "choose-pages"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const fileInput = ref(null);
        const pickTarget = ref(null);
        const dropDepth = ref(0);
        const canAdd = computed(() => !props.disabled && !props.readonly);
        const aiOnlyCount = computed(() => props.docs.filter((doc) => !doc.sendToPayer && doc.links.length > 0).length);
        const addFiles = (files) => {
            if (files.length)
                emit("add-files", files);
        };
        const { pasteFromClipboard, pasting, awaitingPastePrompt, pasteAlert, forgetPaste } = usePastedFiles({
            onFiles: addFiles,
            accept: () => props.accept,
            listen: () => !props.readonly,
            enabled: () => canAdd.value,
        });
        watch(() => [...props.docs], (next, previous) => previous.filter((doc) => !next.includes(doc) && doc.file).forEach((doc) => forgetPaste(doc.file.name)));
        const statusIcon = (doc) => {
            if (doc.links.length)
                return { icon: "mdi-check-circle", color: "success" };
            if (doc.error)
                return { icon: "mdi-alert-circle-outline", color: "error" };
            return { icon: "mdi-file-outline", color: "grey" };
        };
        const pickInto = async (index) => {
            pickTarget.value = index;
            await nextTick();
            fileInput.value?.click();
        };
        const onPick = (event) => {
            const input = event.target;
            const files = Array.from(input.files ?? []);
            input.value = "";
            const target = pickTarget.value;
            pickTarget.value = null;
            if (target === null)
                addFiles(files);
            else if (files[0])
                emit("upload-into", target, files[0]);
        };
        const rowKeys = new WeakMap();
        let nextRowKey = 0;
        const rowKey = (doc) => {
            if (!rowKeys.has(doc))
                rowKeys.set(doc, nextRowKey++);
            return rowKeys.get(doc);
        };
        const dropAlert = ref(null);
        const onDrop = (event) => {
            dropDepth.value = 0;
            dropAlert.value = null;
            if (!canAdd.value)
                return;
            const rules = parseAccept(props.accept);
            const files = Array.from(event.dataTransfer?.files ?? []);
            const accepted = files.filter((file) => matchesAccept(file, rules));
            const skipped = files.length - accepted.length;
            if (skipped) {
                dropAlert.value = `Skipped ${skipped} ${skipped === 1 ? "file" : "files"} of an unsupported type. Supported types: ${describeAccept(rules)}.`;
            }
            addFiles(accepted);
        };
        __expose({ pasting, aiOnlyCount });
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_col = _resolveComponent("v-col");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_checkbox = _resolveComponent("v-checkbox");
            const _component_v_row = _resolveComponent("v-row");
            const _component_v_alert = _resolveComponent("v-alert");
            return (_openBlock(), _createElementBlock("div", null, [
                _cache[12] || (_cache[12] = _createElementVNode("p", { class: "text-body-2 text-medium-emphasis mb-3" }, " Our autocomplete tool reads every file. Checked files are also sent to the payer. ", -1)),
                (__props.readonly && !__props.docs.length)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_1, "No documents uploaded."))
                    : (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        class: _normalizeClass(["doc-list rounded-lg", { 'doc-list--drop': dropDepth.value > 0 && canAdd.value, 'doc-list--empty': !__props.docs.length }]),
                        onDragover: _cache[1] || (_cache[1] = _withModifiers(() => { }, ["prevent"])),
                        onDragenter: _cache[2] || (_cache[2] = _withModifiers(($event) => (dropDepth.value++), ["prevent"])),
                        onDragleave: _cache[3] || (_cache[3] = _withModifiers(($event) => (dropDepth.value = Math.max(0, dropDepth.value - 1)), ["prevent"])),
                        onDrop: _withModifiers(onDrop, ["prevent"])
                    }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.docs, (doc, index) => {
                            return (_openBlock(), _createBlock(_component_v_row, {
                                key: rowKey(doc),
                                "no-gutters": "",
                                align: "center",
                                class: "doc-row px-3"
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_col, {
                                        cols: "12",
                                        sm: "",
                                        class: "d-flex align-center text-truncate pr-2"
                                    }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_icon, {
                                                size: "18",
                                                class: "mr-2 flex-shrink-0",
                                                color: statusIcon(doc).color
                                            }, {
                                                default: _withCtx(() => [
                                                    _createTextVNode(_toDisplayString(statusIcon(doc).icon), 1)
                                                ]),
                                                _: 2
                                            }, 1032, ["color"]),
                                            _createVNode(_component_v_text_field, {
                                                "model-value": doc.label,
                                                variant: "plain",
                                                density: "compact",
                                                "hide-details": "",
                                                "aria-label": "Document name",
                                                class: "text-body-2",
                                                readonly: __props.readonly || doc.requested || doc.labelLocked,
                                                disabled: __props.disabled,
                                                "onUpdate:modelValue": (label) => emit('update-label', index, label)
                                            }, null, 8, ["model-value", "readonly", "disabled", "onUpdate:modelValue"])
                                        ]),
                                        _: 2
                                    }, 1024),
                                    _createVNode(_component_v_col, {
                                        cols: "12",
                                        sm: "auto",
                                        class: "d-flex align-center justify-end"
                                    }, {
                                        default: _withCtx(() => [
                                            (doc.requested)
                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                    (!doc.error)
                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_2, "Requested by the payer"))
                                                        : _createCommentVNode("", true),
                                                    (!__props.readonly)
                                                        ? (_openBlock(), _createBlock(_component_v_btn, {
                                                            key: 1,
                                                            variant: "tonal",
                                                            color: "primary",
                                                            size: "small",
                                                            "prepend-icon": "mdi-paperclip",
                                                            disabled: !canAdd.value,
                                                            onClick: ($event) => (pickInto(index))
                                                        }, {
                                                            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                                                    _createTextVNode(" Upload ", -1)
                                                                ]))]),
                                                            _: 1
                                                        }, 8, ["disabled", "onClick"]))
                                                        : _createCommentVNode("", true),
                                                    (!__props.readonly && doc.error)
                                                        ? (_openBlock(), _createBlock(_component_v_btn, {
                                                            key: 2,
                                                            icon: "mdi-close",
                                                            variant: "text",
                                                            size: "small",
                                                            color: "error",
                                                            "aria-label": `Remove ${doc.label}`,
                                                            disabled: __props.disabled,
                                                            onClick: ($event) => (emit('remove', index))
                                                        }, null, 8, ["aria-label", "disabled", "onClick"]))
                                                        : _createCommentVNode("", true)
                                                ], 64))
                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                    _createVNode(_component_v_checkbox, {
                                                        "model-value": doc.sendToPayer,
                                                        label: "Send to payer",
                                                        density: "compact",
                                                        "hide-details": "",
                                                        class: "doc-row__payer flex-grow-0",
                                                        disabled: __props.disabled || __props.readonly || doc.payerLocked,
                                                        "onUpdate:modelValue": (checked) => emit('update-send-to-payer', index, checked === true)
                                                    }, null, 8, ["model-value", "disabled", "onUpdate:modelValue"]),
                                                    _createVNode(_component_v_btn, {
                                                        href: __props.openLink ? undefined : doc.links[0],
                                                        disabled: !doc.links.length,
                                                        target: __props.openLink ? undefined : '_blank',
                                                        icon: "mdi-eye-outline",
                                                        variant: "text",
                                                        size: "small",
                                                        color: "primary",
                                                        "aria-label": `View ${doc.label}`,
                                                        onClick: ($event) => (__props.openLink && __props.openLink(doc))
                                                    }, null, 8, ["href", "disabled", "target", "aria-label", "onClick"]),
                                                    (!__props.readonly)
                                                        ? (_openBlock(), _createBlock(_component_v_btn, {
                                                            key: 0,
                                                            icon: "mdi-close",
                                                            variant: "text",
                                                            size: "small",
                                                            color: "error",
                                                            "aria-label": `Remove ${doc.label}`,
                                                            disabled: __props.disabled,
                                                            onClick: ($event) => (emit('remove', index))
                                                        }, null, 8, ["aria-label", "disabled", "onClick"]))
                                                        : _createCommentVNode("", true)
                                                ], 64))
                                        ]),
                                        _: 2
                                    }, 1024),
                                    _createVNode(_component_v_col, {
                                        cols: "12",
                                        class: "ps-7"
                                    }, {
                                        default: _withCtx(() => [
                                            _createVNode(ChartPrepareStatus, {
                                                preparing: doc.uploading || doc.preparing,
                                                recompressing: doc.recompressing,
                                                error: doc.error,
                                                "dropped-pages": doc.droppedPages,
                                                "origin-bytes": doc.originBytes,
                                                "size-bytes": doc.sizeBytes,
                                                "compression-level": doc.level,
                                                "can-choose-pages": __props.canChoosePages(doc),
                                                onChoosePages: ($event) => (emit('choose-pages', index))
                                            }, null, 8, ["preparing", "recompressing", "error", "dropped-pages", "origin-bytes", "size-bytes", "compression-level", "can-choose-pages", "onChoosePages"])
                                        ]),
                                        _: 2
                                    }, 1024)
                                ]),
                                _: 2
                            }, 1024));
                        }), 128)),
                        (!__props.readonly)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: _normalizeClass(["doc-list__add d-flex align-center ga-2", __props.docs.length ? 'flex-wrap px-3 py-2' : 'flex-column pa-6'])
                            }, [
                                (!__props.docs.length)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        _createVNode(_component_v_icon, {
                                            size: "28",
                                            color: "primary"
                                        }, {
                                            default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                                                    _createTextVNode("mdi-tray-arrow-up", -1)
                                                ]))]),
                                            _: 1
                                        }),
                                        _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-body-2 d-none d-sm-inline" }, "Drag files here, or", -1))
                                    ], 64))
                                    : _createCommentVNode("", true),
                                _createElementVNode("div", _hoisted_3, [
                                    _createVNode(_component_v_btn, {
                                        variant: "tonal",
                                        color: "primary",
                                        size: "default",
                                        "prepend-icon": "mdi-paperclip",
                                        disabled: !canAdd.value,
                                        onClick: _cache[0] || (_cache[0] = ($event) => (pickInto(null)))
                                    }, {
                                        default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                                _createTextVNode(" Choose files ", -1)
                                            ]))]),
                                        _: 1
                                    }, 8, ["disabled"]),
                                    _createVNode(_component_v_btn, {
                                        variant: "text",
                                        color: "primary",
                                        size: "default",
                                        "prepend-icon": "mdi-clipboard-outline",
                                        loading: _unref(pasting),
                                        disabled: !canAdd.value,
                                        onClick: _unref(pasteFromClipboard)
                                    }, {
                                        default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                                                _createTextVNode(" Paste screenshot ", -1)
                                            ]))]),
                                        _: 1
                                    }, 8, ["loading", "disabled", "onClick"])
                                ]),
                                (!__props.docs.length)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_4, "PDF, PNG, JPG or TIFF"))
                                    : _createCommentVNode("", true)
                            ], 2))
                            : _createCommentVNode("", true),
                        _createElementVNode("input", {
                            ref_key: "fileInput",
                            ref: fileInput,
                            type: "file",
                            multiple: pickTarget.value === null,
                            accept: __props.accept,
                            class: "d-none",
                            onChange: onPick
                        }, null, 40, _hoisted_5)
                    ], 34)),
                (_unref(awaitingPastePrompt))
                    ? (_openBlock(), _createElementBlock("p", _hoisted_6, [...(_cache[9] || (_cache[9] = [
                            _createTextVNode(" Waiting for the browser. Click ", -1),
                            _createElementVNode("strong", null, "Paste", -1),
                            _createTextVNode(" in the prompt it just opened. ", -1)
                        ]))]))
                    : _createCommentVNode("", true),
                (_unref(pasteAlert))
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 3,
                        type: _unref(pasteAlert).type,
                        variant: "tonal",
                        density: "compact",
                        class: "mt-2"
                    }, {
                        default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(pasteAlert).text), 1)
                        ]),
                        _: 1
                    }, 8, ["type"]))
                    : _createCommentVNode("", true),
                (dropAlert.value)
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 4,
                        type: "warning",
                        variant: "tonal",
                        density: "compact",
                        class: "mt-2"
                    }, {
                        default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(dropAlert.value), 1)
                        ]),
                        _: 1
                    }))
                    : _createCommentVNode("", true),
                (__props.budgetCaption)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(__props.budgetCaption), 1))
                    : _createCommentVNode("", true),
                (aiOnlyCount.value > _unref(MAX_AI_ONLY_DOCUMENTS))
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 6,
                        type: "warning",
                        variant: "tonal",
                        density: "compact",
                        class: "mt-2"
                    }, {
                        default: _withCtx(() => [
                            _createTextVNode(" At most " + _toDisplayString(_unref(MAX_AI_ONLY_DOCUMENTS)) + " files can be for our autocomplete tool only. Check ", 1),
                            _cache[10] || (_cache[10] = _createElementVNode("strong", null, "Send to payer", -1)),
                            _cache[11] || (_cache[11] = _createTextVNode(" on some, or remove some. ", -1))
                        ]),
                        _: 1
                    }))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
